/**
 * Team section
 * Purpose: Render a reusable, typed team grid with editable images and social links.
 *
 * Notes:
 * - Keeps image header square and clipped to a circular/square area.
 * - Supports a compact layout variant used on the Team page.
 * - Stores and loads team images via TeamStorage helpers.
 * - Adds a subtle fade-in for the expertise line on hover (visible by default on md+ screens).
 */

import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { Linkedin, Mail } from 'lucide-react'
import SectionHeader from '../common/SectionHeader'
import { Button } from '../ui/button'
import {
  getTeamMemberImage,
  updateTeamMemberImage,
  saveTeamImages
} from '../team/TeamStorage'
import ImageUploadModal from '../team/ImageUploadModal'

/**
 * Single team member model for the grid
 * - name: display name
 * - position: role title
 * - image: image URL (smart placeholder allowed)
 * - linkedin: optional LinkedIn profile URL
 * - email: optional email address
 * - expertise: optional short expertise / experience line shown under the position
 */
export interface TeamMember {
  /** Display name */
  name: string
  /** Position / role title */
  position: string
  /** Image URL (smart placeholder allowed) */
  image: string
  /** Optional LinkedIn URL */
  linkedin?: string
  /** Optional email address */
  email?: string
  /** Short expertise/experience line to show under the position */
  expertise?: string
}

/**
 * Controls image sizing and grid spacing without changing layout semantics
 */
export type TeamSectionSize = 'default' | 'compact'

/**
 * Props to control rendering and content
 */
interface TeamSectionProps {
  /** Team members to render */
  items?: TeamMember[]
  /** Optional custom title */
  title?: string
  /** Force visibility regardless of route (e.g., used by Team page) */
  forceVisible?: boolean
  /** Optional visual size; compact makes cards narrower and increases card spacing */
  size?: TeamSectionSize
  /** Allow image upload UI overlay on each member card */
  showImageUpload?: boolean
}

/**
 * Internal: card for each team member
 * - Image header remains square and fills the card (no transform scaling)
 * - Compact mode reduces overall card width via grid columns, not image scaling
 * - Uses stored images when available
 *
 * @param props.member TeamMember data to render
 * @param props.showUpload Whether to show upload UI overlay
 * @param props.onImageUpdate Callback when image is updated
 * @returns JSX.Element
 */
function MemberCard({
  member,
  showUpload = false,
  onImageUpdate
}: {
  member: TeamMember
  showUpload?: boolean
  onImageUpdate?: (name: string, imageUrl: string) => void
}): JSX.Element {
  // Keep image at natural scale; the card becomes smaller via grid column count
  const imageScale = 1
  const [currentImage, setCurrentImage] = useState(member.image)

  // Load stored image on component mount
  useEffect(() => {
    const storedImage = getTeamMemberImage(member.name, member.image)
    setCurrentImage(storedImage)
  }, [member.name, member.image])

  /**
   * Handle image updates from the upload modal
   * - Persist new image to local storage via TeamStorage helpers
   */
  const handleImageUpdate = (name: string, imageUrl: string) => {
    updateTeamMemberImage(name, imageUrl)
    setCurrentImage(imageUrl)
    onImageUpdate?.(name, imageUrl)
  }

  return (
    <div className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-shadow duration-300 overflow-hidden">
      {/* Image header (square) */}
      <div className="relative w-full aspect-square overflow-hidden bg-gray-100 flex items-center justify-center group">
        <img
          src={currentImage}
          alt={`${member.name} portrait`}
          className="object-cover w-full h-full"
          style={{ transform: `scale(${imageScale})`, transformOrigin: 'center center' }}
          onError={(e) => {
            // Fallback to placeholder if image fails to load
            e.currentTarget.src =
              'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/6b09378d-d180-4898-b383-bc968a721f07.jpg'
          }}
        />
        {/* Subtle gradient for readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" aria-hidden="true" />

        {/* Upload overlay (only shown on hover when upload is enabled) */}
        {showUpload && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <ImageUploadModal teamMember={member} onImageUpdate={handleImageUpdate} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{member.name}</h3>
            <p className="text-sm text-gray-600">{member.position}</p>
            {/* Expertise / experience line (if provided)
                - Hidden by default on small screens, fades in on card hover
                - Visible by default on md+ screens for accessibility and readability
            */}
            {member.expertise && (
              <p className="text-xs italic text-gray-500 mt-1 opacity-0 group-hover:opacity-100 md:opacity-100 transition-opacity duration-300 ease-out">
                {member.expertise}
              </p>
            )}
          </div>
          {/* Accent dot */}
          <span className="mt-1 inline-block h-2.5 w-2.5 rounded-full bg-orange-500" aria-hidden="true" />
        </div>

        {/* Socials (render only if provided) */}
        <div className="mt-5 flex items-center gap-2">
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors"
              aria-label={`${member.name} on LinkedIn`}
            >
              <Linkedin className="h-4 w-4" />
            </a>
          )}
          {member.email && (
            <a
              href={`mailto:${member.email}`}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:text-orange-600 hover:border-orange-300 transition-colors"
              aria-label={`Email ${member.name}`}
            >
              <Mail className="h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Team section: visible on /team by default; reusable elsewhere
 *
 * @param props TeamSectionProps
 * @returns JSX.Element | null
 */
export default function TeamSection(props: TeamSectionProps = {}): JSX.Element | null {
  const {
    items,
    title = 'Experienced. Multidisciplinary. Focused on Results.',
    forceVisible,
    size = 'default',
    showImageUpload = false
  } = props
  const location = useLocation()

  // Only show on /team unless forced visible
  const show = forceVisible ?? location.pathname === '/team'
  if (!show) return null

  // Save initial team images to storage when component mounts
  useEffect(() => {
    if (items && items.length > 0) {
      saveTeamImages(items)
    }
  }, [items])

  // Fallback minimal dataset for safety (not used on the Team page which passes items)
  const fallback: TeamMember[] = [
    {
      name: 'Team Member',
      position: 'Specialist',
      image:
        'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/7c1eae55-d7de-4c41-be77-803c8d52b200.jpg'
    },
    {
      name: 'Senior Expert',
      position: 'Lead',
      image:
        'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/0ff47192-7474-4d03-bca7-5ce47bd75451.jpg'
    },
    {
      name: 'Coordinator',
      position: 'Coordinator',
      image:
        'https://pub-cdn.sider.ai/u/U03VH4X1R0A/web-coder/68934572f1d36a2a58005e71/resource/5851a9e3-cbcf-4474-8239-153e954edc03.jpg'
    }
  ]

  const teamToRender = items && items.length > 0 ? items : fallback

  // Gap and columns:
  // - default: 1/2/3 columns as before
  // - compact: make cards smaller by showing more columns; keep gap to 30px
  const gapClass = size === 'compact' ? 'gap-[30px]' : 'gap-6'
  const colsClass =
    size === 'compact' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Meet the Team"
          title={title}
          subtitle="Our senior specialists partner with you from strategy to execution across digital and traditional media."
        />

        {/* Grid */}
        <div className={`mt-12 grid ${colsClass} ${gapClass}`}>
          {teamToRender.map((m) => (
            <MemberCard
              key={`${m.name}-${m.position}`}
              member={m}
              showUpload={showImageUpload}
              onImageUpdate={(name, imageUrl) => {
                console.log(`Updated image for ${name}:`, imageUrl)
              }}
            />
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 flex justify-center">
          <Button asChild variant="outline" className="bg-transparent border-orange-300 text-orange-700 hover:bg-orange-50">
            <Link to="/about">Learn more about Edicoo</Link>
          </Button>
        </div>
      </div>
    </section>
  )
}